import { useMemo } from "react";
import { StreamShape } from "@sablier/v2-constants";
import { Translate } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Stream } from "@sablier/v2-models";
import { vendors } from "@sablier/v2-utils";
import {
  IExtensionDependencies,
  IExtensionOverrides,
  IExtensionParamsGroup,
  IExtensionParamsSingle,
  IExtensionResultGroup,
  IExtensionResultSingle,
} from "~/client/types";
import type { ComponentType, FC } from "react";
import config, { dynamic, linear } from "./config";
import {
  Extension,
  ICheck,
  IExtension,
  IExtensionConstructorParams,
} from "./setup";

function initialize(params: IExtensionConstructorParams): {
  extension: IExtension | undefined;
  overrides: IExtensionOverrides | undefined;
} {
  if (!_.isNilOrEmptyString(params.shape)) {
    const result = Object.values(config).find(
      (extension) =>
        extension.shape.toLowerCase() === params.shape!.toLowerCase(),
    );
    if (result) {
      return {
        extension: result.initial(params),
        overrides: result.overrides?.(),
      };
    }
  }
  return {
    extension: { purpose: undefined },
    overrides: undefined,
  };
}

function reset(purpose?: Extension): IExtension | undefined {
  if (!_.isNilOrEmptyString(purpose)) {
    return config[purpose].initial({});
  }
  return undefined;
}

function Single({ purpose }: { purpose?: Extension }) {
  if (!_.isNilOrEmptyString(purpose) && !_.isNil(config[purpose].Form.Single)) {
    const Single = config[purpose].Form.Single as FC;
    return <Single />;
  }
  return <></>;
}

function Group({
  purpose,
  streamId,
}: {
  purpose?: Extension;
  streamId: string;
}) {
  if (!_.isNilOrEmptyString(purpose) && !_.isNil(config[purpose].Form.Group)) {
    const Group = config[purpose].Form.Group as ComponentType<{
      streamId: string;
    }>;
    return <Group streamId={streamId} />;
  }
  return <></>;
}

function GroupGeneral({ purpose }: { purpose?: Extension }) {
  if (
    !_.isNilOrEmptyString(purpose) &&
    !_.isNil(config[purpose].Form.GroupGeneral)
  ) {
    const GroupGeneral = config[purpose].Form.GroupGeneral as FC;
    return <GroupGeneral />;
  }
  return <></>;
}

function check({
  t,
  data,
  isLoadingIncluded,
  isWarningIncluded,
  timing,
}: ICheck): string | undefined {
  const { purpose } = data;
  if (!_.isNilOrEmptyString(purpose) && !_.isNil(config[purpose].check)) {
    return config[purpose].check!({
      t,
      data,
      isLoadingIncluded,
      isWarningIncluded,
      timing,
    });
  }
  return undefined;
}

function identify(stream: Stream): Extension | StreamShape | undefined {
  if (!_.isNil(stream)) {
    for (const key in config) {
      if (!_.isNil(config[key].identify) && config[key].identify(stream)) {
        return key;
      }
    }
  }

  return undefined;
}

function processSingle({
  dependencies,
  extras,
  timing,
  permit2,
}: IExtensionParamsSingle): Promise<IExtensionResultSingle> | undefined {
  try {
    const purpose = extras?.purpose as Extension;

    if (
      !_.isNilOrEmptyString(purpose) &&
      !_.isNil(config[purpose]) &&
      _.get(config[purpose], "process.single")
    ) {
      return config[purpose].process.single({
        dependencies,
        extras,
        timing,
        permit2,
      });
    }
  } catch (error) {
    vendors.crash.log(error);
  }

  return undefined;
}

function processGroup({
  dependencies,
  purpose,
  timing,
  permit2,
}: IExtensionParamsGroup): Promise<IExtensionResultGroup> | undefined {
  try {
    if (
      !_.isNilOrEmptyString(purpose) &&
      !_.isNil(config[purpose]) &&
      _.get(config[purpose], "process.group")
    ) {
      return config[purpose].process.group({
        dependencies,
        purpose,
        timing,
        permit2,
      });
    }
  } catch (error) {
    vendors.crash.log(error);
  }

  return undefined;
}

function summary(
  dependencies: IExtensionDependencies,
  t: Translate,
  extension?: IExtension,
) {
  try {
    const purpose = extension?.purpose;
    if (
      !_.isNilOrEmptyString(purpose) &&
      !_.isNil(config[purpose]) &&
      !_.isNil(config[purpose].summary)
    ) {
      return config[purpose].summary(dependencies, extension, t);
    }
  } catch (error) {
    vendors.crash.log(error);
  }

  return undefined;
}

function simulate({ dependencies, extras, timing }: IExtensionParamsSingle) {
  try {
    const purpose = extras?.purpose as Extension;

    if (
      !_.isNilOrEmptyString(purpose) &&
      !_.isNil(config[purpose]) &&
      _.get(config[purpose], "simulate")
    ) {
      return config[purpose].simulate({ dependencies, extras, timing });
    }
  } catch (error) {
    vendors.crash.log(error);
  }

  return undefined;
}

function precomputeSingle({
  dependencies,
  extras,
  timing,
}: IExtensionParamsSingle) {
  try {
    const purpose = extras?.purpose as Extension;

    if (
      !_.isNilOrEmptyString(purpose) &&
      !_.isNil(config[purpose]) &&
      _.get(config[purpose], "precompute.single")
    ) {
      return config[purpose].precompute.single({
        dependencies,
        extras,
        timing,
      });
    }
  } catch (error) {
    vendors.crash.log(error);
  }

  return undefined;
}

function precomputeGroup({
  dependencies,
  timing,
  purpose,
}: IExtensionParamsGroup) {
  try {
    if (
      !_.isNilOrEmptyString(purpose) &&
      !_.isNil(config[purpose]) &&
      _.get(config[purpose], "precompute.group")
    ) {
      return config[purpose].precompute.group({
        purpose,
        dependencies,
        timing,
      });
    }
  } catch (error) {
    vendors.crash.log(error);
  }

  return undefined;
}

/**
 * 🤷‍♂️ | 🤷‍♂️ |
 * There's an interesting behavior with this hook, requiring a seemingly useless useMemo
 * Even if it's returning a constant object, it causes any effect that depends on it to update again and again.
 *
 * @returns Pair of utility methods {initialize, reset}
 */
function useExtensionUtilities() {
  return useMemo(
    () => ({
      initialize,
      reset,
    }),
    [],
  );
}

const bundle = {
  check,
  process: {
    single: processSingle,
    group: processGroup,
  },
  initialize,
  summary,
  identify,
  simulate,
  precompute: {
    single: precomputeSingle,
    group: precomputeGroup,
  },
};

export {
  Extension,
  Group,
  GroupGeneral,
  Single,
  useExtensionUtilities,
  linear,
  dynamic,
};
export type { IExtension };
export default bundle;
